export type AuditActionGroup =
  | "INSTALLATION"
  | "ACCESS"
  | "STORE"
  | "POLICY"
  | "COMPETITOR"
  | "MAPPING"
  | "REQUEST"
  | "OFFER"
  | "ADMIN_NOTE"
  | "SYSTEM_ADMIN";

export type AuditDateWindow = "LAST_7_DAYS" | "LAST_15_DAYS" | "LAST_30_DAYS" | "CUSTOM";
export type AuditActorTypeFilter = "" | "MERCHANT_USER" | "SHOPIFY" | "SYSTEM" | "SYSTEM_ADMIN";
export type AuditEntityTypeFilter =
  | ""
  | "Store"
  | "Product"
  | "Policy"
  | "Competitor"
  | "Mapping"
  | "Request"
  | "Offer"
  | "Widget"
  | "User"
  | "Job";

export interface AuditActorContext {
  actorType: string;
  actorId?: string;
  displayName?: string;
}

export interface AuditEntry {
  id: string;
  storeHash?: string;
  actionGroup: AuditActionGroup;
  action: string;
  actionLabel?: string;
  actor: AuditActorContext;
  targetEntityType: string;
  targetEntityId: string;
  targetEntityLabel?: string;
  safeSummary?: string;
  reason?: string;
  message?: string;
  beforeSummary?: Record<string, unknown>;
  afterSummary?: Record<string, unknown>;
  correlationId?: string;
  createdAt: string;
  supportInvolved: boolean;
  supportIndicator?: string;
  immutable: true;
}

export interface AuditFilters {
  search?: string;
  dateWindow?: AuditDateWindow;
  actorType?: AuditActorTypeFilter;
  actionGroup?: AuditActionGroup | "";
  entityType?: AuditEntityTypeFilter;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

export interface AuditFiltersViewModel {
  search: string;
  dateWindow: AuditDateWindow;
  actorType: AuditActorTypeFilter;
  actionGroup: AuditActionGroup | "";
  entityType: AuditEntityTypeFilter;
  from: string;
  to: string;
  limit: number;
  cursor?: string;
}

export interface AuditPage {
  entries: AuditEntry[];
  nextCursor: string | null;
  requestId?: string;
}

export interface AuditRowViewModel {
  id: string;
  timestampLabel: string;
  actorLabel: string;
  actionLabel: string;
  actionClassName: string;
  entityTypeLabel: string;
  entityLabel: string;
  safeSummary: string;
  supportInvolved: boolean;
}

export interface AuditDetailViewModel {
  id: string;
  actorLabel: string;
  actorTypeLabel: string;
  actionLabel: string;
  entityLabel: string;
  timestampLabel: string;
  reasonLabel?: string;
  messageLabel?: string;
  correlationId?: string;
  supportIndicator?: string;
  beforeRows: AuditSummaryRow[];
  afterRows: AuditSummaryRow[];
}

export interface AuditSummaryRow {
  key: string;
  value: string;
}

export interface AuditPaginationViewModel {
  nextCursor: string | null;
  canGoNext: boolean;
  staleCursor: boolean;
}

export type AuditSectionStateStatus =
  "EMPTY" | "FAILED" | "PERMISSION_DENIED" | "PARTIAL" | "STALE_CURSOR" | "NOT_FOUND";

export interface AuditSectionStateViewModel {
  id: "table" | "detail";
  status: AuditSectionStateStatus;
  message: string;
  requestId?: string;
}

export interface AuditPageViewModel {
  appliedFilters: AuditFiltersViewModel;
  draftFilters: AuditFiltersViewModel;
  rows: AuditRowViewModel[];
  pagination: AuditPaginationViewModel;
  selectedEntry?: AuditDetailViewModel;
  sectionStates: AuditSectionStateViewModel[];
  requestId?: string;
}

export const AUDIT_ACTION_GROUPS: AuditActionGroup[] = [
  "INSTALLATION",
  "ACCESS",
  "STORE",
  "POLICY",
  "COMPETITOR",
  "MAPPING",
  "REQUEST",
  "OFFER",
  "ADMIN_NOTE",
  "SYSTEM_ADMIN",
];

export const auditDateWindowOptions: Array<{ value: AuditDateWindow; label: string }> = [
  { value: "LAST_7_DAYS", label: "Date: Last 7 days" },
  { value: "LAST_15_DAYS", label: "Date: Last 15 days" },
  { value: "LAST_30_DAYS", label: "Date: Last 30 days" },
  { value: "CUSTOM", label: "Date: Custom" },
];

export const auditActorOptions: Array<{ value: AuditActorTypeFilter; label: string }> = [
  { value: "", label: "Actor: All" },
  { value: "MERCHANT_USER", label: "Merchant user" },
  { value: "SHOPIFY", label: "Platform" },
  { value: "SYSTEM", label: "Price Rescue system" },
  { value: "SYSTEM_ADMIN", label: "Authorized support" },
];

export const auditActionGroupOptions: Array<{ value: AuditActionGroup | ""; label: string }> = [
  { value: "", label: "Action: All" },
  ...AUDIT_ACTION_GROUPS.map((value) => ({ value, label: titleCase(value) })),
];

export const auditEntityOptions: Array<{ value: AuditEntityTypeFilter; label: string }> = [
  { value: "", label: "Entity: All" },
  { value: "Store", label: "Store" },
  { value: "Product", label: "Product" },
  { value: "Policy", label: "Pricing policy" },
  { value: "Competitor", label: "Competitor" },
  { value: "Mapping", label: "Mapping" },
  { value: "Request", label: "Price-match request" },
  { value: "Offer", label: "Offer" },
  { value: "Widget", label: "Widget" },
  { value: "User", label: "Merchant user" },
  { value: "Job", label: "Job" },
];

export function defaultAuditFilters(): AuditFiltersViewModel {
  return {
    search: "",
    dateWindow: "LAST_30_DAYS",
    actorType: "",
    actionGroup: "",
    entityType: "",
    from: "",
    to: "",
    limit: 25,
  };
}

export function toAuditFilters(filters: AuditFiltersViewModel): AuditFilters {
  return {
    limit: filters.limit,
    dateWindow: filters.dateWindow,
    ...(filters.search.trim().length === 0 ? {} : { search: filters.search.trim() }),
    ...(filters.actorType === "" ? {} : { actorType: filters.actorType }),
    ...(filters.actionGroup === "" ? {} : { actionGroup: filters.actionGroup }),
    ...(filters.entityType === "" ? {} : { entityType: filters.entityType }),
    ...(filters.dateWindow !== "CUSTOM" || filters.from.length === 0 ? {} : { from: filters.from }),
    ...(filters.dateWindow !== "CUSTOM" || filters.to.length === 0 ? {} : { to: filters.to }),
    ...(filters.cursor === undefined ? {} : { cursor: filters.cursor }),
  };
}

export function summaryRows(summary: Record<string, unknown> | undefined): AuditSummaryRow[] {
  return Object.entries(summary ?? {})
    .filter(([key]) => !isSensitiveSummaryKey(key))
    .map(([key, value]) => ({ key: titleCase(key), value: safeSummaryValue(value) }));
}

export function safeSummaryValue(value: unknown): string {
  if (value === null || value === undefined) return "N/A";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function titleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(
      /(^|_)([a-z])/g,
      (_match, separator: string, letter: string) =>
        `${separator === "_" ? " " : ""}${letter.toUpperCase()}`,
    );
}

function isSensitiveSummaryKey(key: string): boolean {
  return /(token|secret|password|credential|authorization|ciphertext|privatekey|cookie|csrf|customer|email|phone|address|payload|evidence|cost|margin)/i.test(
    key,
  );
}
